import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { commonStyles } from "@/lib/theme";
import { supabase, ensureTasksTable } from "@/lib/supabase";
import { Check, Trash2, GripVertical, Edit2, RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/AuthContext";

interface Task {
  id: string;
  title: string;
  type: 'job' | 'generic' | 'custom';
  time: string; // HH:mm format
  completed: boolean;
  date: string; // yyyy-MM-dd format
  jobId?: string;
  user_id?: string; // Add user_id field to match database schema
}

interface DayDetailProps {
  date: Date | null;
  isOpen: boolean;
  onClose: () => void;
  isDarkMode?: boolean;
  demoMode?: boolean;
}

const timeSlots = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return `${hour}:00`;
});

const isValidTime = (time: string): boolean => {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};

export const DayDetail = ({ date, isOpen, onClose, isDarkMode = true, demoMode = false }: DayDetailProps) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [timeError, setTimeError] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Helper function to get the next available time slot
  const getNextAvailableTime = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const nextHour = (currentHour + 1) % 24; // Make sure we don't go over 23
    return `${nextHour.toString().padStart(2, '0')}:00`;
  };

  // Initialize with the next available time slot when component loads or dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTime(getNextAvailableTime());
    }
  }, [isOpen]);

  // Reset selectedTime when task title is cleared
  useEffect(() => {
    if (newTaskTitle === "" && !editingTask) {
      setSelectedTime(getNextAvailableTime());
    }
  }, [newTaskTitle, editingTask]);

  // Add console log to check demoMode
  console.log('DayDetail - demoMode:', demoMode);
  console.log('DayDetail - user:', user?.id || 'No user logged in');

  useEffect(() => {
    if (date) {
      console.log(`Date changed to: ${format(date, 'yyyy-MM-dd')}`);
      
      if (user || demoMode) {
        console.log('User or demoMode available, fetching tasks');
      fetchTasks();
      } else {
        console.log('No user and not in demoMode, cannot fetch tasks');
      }
    } else {
      console.log('No date selected, skipping task fetch');
    }
  }, [date, user, demoMode]);

  const fetchTasks = async () => {
    if (!date) {
      console.log('No date provided, cannot fetch tasks');
      return;
    }
    
    if (demoMode) {
      // In demo mode, we use local state only with some sample tasks
      console.log('fetchTasks in demo mode - initializing with sample tasks');
      const formattedDate = format(date, 'yyyy-MM-dd');
      const demoTasks: Task[] = [
        {
          id: 'demo-1',
          title: 'Review job application',
          type: 'job',
          time: '09:00',
          completed: false,
          date: formattedDate
        },
        {
          id: 'demo-2',
          title: 'Prepare for interview',
          type: 'job',
          time: '13:00',
          completed: true,
          date: formattedDate
        },
        {
          id: 'demo-3',
          title: 'Research company',
          type: 'custom',
          time: '15:30',
          completed: false,
          date: formattedDate
        }
      ];
      setTasks(demoTasks);
      return;
    }

    if (!user) {
      console.log('No user found, cannot fetch tasks');
      toast({
        title: "Authentication required",
        description: "Please sign in to view tasks.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Ensure the tasks table exists before trying to query it
      const tablesReady = await ensureTasksTable();
      
      if (!tablesReady) {
        console.error('Tasks table could not be created or verified');
        toast({
          title: "Database setup issue",
          description: "The tasks feature is not available. Please contact support.",
          variant: "destructive",
        });
        return;
      }
      
      const formattedDate = format(date, 'yyyy-MM-dd');
      console.log(`Fetching tasks for date: ${formattedDate} and user: ${user.id}`);
      
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('date', formattedDate)
        .eq('user_id', user.id);

      if (error) {
        console.error('Database error when fetching tasks:', error);
        throw error;
      }

      console.log(`Retrieved ${data?.length || 0} tasks:`, data);
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: "Error fetching tasks",
        description: "Could not load your tasks. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTimeChange = (value: string) => {
    setTimeError(false);
    // Allow partial input while typing
    if (value.length <= 5) {
      setSelectedTime(value);
    }
  };

  const addTask = async (type: Task['type'] = 'custom') => {
    console.log('addTask called with type:', type);
    console.log('demoMode:', demoMode);
    console.log('user:', user);
    console.log('newTaskTitle:', newTaskTitle);
    console.log('selectedTime:', selectedTime);
    console.log('isValidTime:', isValidTime(selectedTime));
    console.log('date:', date);

    if (!date) {
      console.log('No date selected, returning');
      return;
    }
    if (type === 'custom' && !newTaskTitle.trim()) {
      console.log('No task title for custom task, returning');
      return;
    }
    if (!isValidTime(selectedTime)) {
      console.log('Invalid time format');
      setTimeError(true);
      toast({
        title: "Invalid time format",
        description: "Please enter time in HH:mm format (e.g., 09:15)",
        variant: "destructive",
      });
      return;
    }

    const formattedDate = format(date, 'yyyy-MM-dd');
    console.log('Formatted date:', formattedDate);

    const newTask: Omit<Task, 'id'> = {
      title: type === 'custom' ? newTaskTitle : 'Generic Task',
      type,
      time: selectedTime,
      completed: false,
      date: formattedDate,
    };
    console.log('New task object:', newTask);

    if (demoMode) {
      console.log('Adding task in demo mode');
      // In demo mode, just update the local state with a mock ID
      const mockId = `demo-${Date.now()}`;
      console.log('Generated mock ID:', mockId);
      
      // Create a complete task object with the mock ID
      const demoTask: Task = {
        ...newTask,
        id: mockId,
      };
      
      console.log('Demo task to be added:', demoTask);
      
      // Update the tasks state with the new demo task
      setTasks(prev => {
        const newTasks = [...prev, demoTask];
        console.log('Updated tasks in demo mode:', newTasks);
        return newTasks;
      });
      
      // Reset form state
      setNewTaskTitle("");
      // Set time for next task to the next hour
      setSelectedTime(getNextAvailableTime());
      
      toast({
        title: "Demo Mode",
        description: "Task added in demo mode.",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to add tasks.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Ensure the tasks table exists before trying to add to it
      const tablesReady = await ensureTasksTable();
      
      if (!tablesReady) {
        console.error('Tasks table could not be created or verified');
        toast({
          title: "Database setup issue",
          description: "The tasks feature is not available. Please contact support.",
          variant: "destructive",
        });
        return;
      }
      
      // Prepare the task data with the user_id and ensure all fields match the database schema
      const taskData = {
        title: type === 'custom' ? newTaskTitle : 'Generic Task',
        type,
        time: selectedTime,
        completed: false,
        date: formattedDate,
        user_id: user.id,
        created_at: new Date().toISOString()
      };
      
      console.log('Sending task to database:', taskData);
      
      // First check if the table exists and is accessible
      const { data: tableCheck, error: tableError } = await supabase
        .from('tasks')
        .select('id')
        .limit(1);
        
      if (tableError) {
        console.error('Error checking tasks table:', tableError);
        toast({
          title: "Database error",
          description: "Could not access the tasks table. Please try again later.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Tasks table accessible, proceeding with insert');
      
      const { data, error } = await supabase
        .from('tasks')
        .insert([taskData])
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        
        if (error.code === '23502') { // not-null constraint violation
          toast({
            title: "Error adding task",
            description: "Missing required field(s). Please try again.",
            variant: "destructive",
          });
        } else if (error.code === '42P01') { // undefined_table
          toast({
            title: "Database setup issue",
            description: "The tasks table doesn't exist. Please contact support.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error adding task",
            description: "Could not add the task. Please try again.",
            variant: "destructive",
          });
        }
        
        throw error;
      }

      console.log('Task successfully added to database:', data);
      setTasks(prev => [...prev, data as Task]);
      setNewTaskTitle("");
      // Set time for next task to the next hour
      setSelectedTime(getNextAvailableTime());
      
      toast({
        title: "Task added",
        description: "Your task has been added successfully.",
      });
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: "Error adding task",
        description: "Could not add the task. Please try again.",
        variant: "destructive",
      });
    }
  };

  const toggleTaskCompletion = async (taskId: string) => {
    const taskIndex = tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) return;

    const updatedTask = { ...tasks[taskIndex], completed: !tasks[taskIndex].completed };
    console.log('Toggling task completion:', updatedTask);

    if (demoMode) {
      // In demo mode, just update the local state
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
      return;
    }

    if (!user) {
      console.log('No user found, cannot update task');
      return;
    }
    
    try {
      console.log('Updating task in database:', taskId, updatedTask.completed);
      
      const { error } = await supabase
        .from('tasks')
        .update({ completed: updatedTask.completed })
        .eq('id', taskId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Database error when updating task:', error);
        throw error;
      }

      console.log('Task successfully updated in database');
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: "Error updating task",
        description: "Could not update the task status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deleteTask = async (taskId: string) => {
    console.log('Attempting to delete task:', taskId);
    
    if (demoMode) {
      // In demo mode, just update the local state
      console.log('Deleting task in demo mode');
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast({
        title: "Demo Mode",
        description: "Task deleted in demo mode.",
      });
      return;
    }

    if (!user) {
      console.log('No user found, cannot delete task');
      return;
    }

    try {
      console.log('Deleting task from database:', taskId);
      
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Database error when deleting task:', error);
        throw error;
      }

      console.log('Task successfully deleted from database');
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast({
        title: "Task deleted",
        description: "The task has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error deleting task",
        description: "Could not delete the task. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditingTask = (task: Task) => {
    setEditingTask(task);
    setNewTaskTitle(task.title);
    setSelectedTime(task.time);
  };

  const updateTask = async () => {
    if (!editingTask) return;
    
    if (!isValidTime(selectedTime)) {
      setTimeError(true);
      toast({
        title: "Invalid time format",
        description: "Please enter time in HH:mm format (e.g., 09:15)",
        variant: "destructive",
      });
      return;
    }

    if (demoMode) {
      setTasks(prev => prev.map(task => 
        task.id === editingTask.id 
          ? { ...task, title: newTaskTitle, time: selectedTime } 
          : task
      ));
      
      setEditingTask(null);
      setNewTaskTitle("");
      setSelectedTime(getNextAvailableTime());
      toast({
        title: "Demo Mode",
        description: "Task updated in demo mode (not saved to database).",
      });
      return;
    }

    if (!user) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .update({ 
          title: newTaskTitle,
          time: selectedTime
        })
        .eq('id', editingTask.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setTasks(prev => prev.map(task => 
        task.id === editingTask.id 
          ? { ...task, title: newTaskTitle, time: selectedTime } 
          : task
      ));
      
      setEditingTask(null);
      setNewTaskTitle("");
      setSelectedTime(getNextAvailableTime());
      toast({
        title: "Task updated",
        description: "Your task has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: "Error updating task",
        description: "Could not update the task. Please try again.",
        variant: "destructive",
      });
    }
  };

  const cancelEditing = () => {
    setEditingTask(null);
    setNewTaskTitle("");
    setSelectedTime(getNextAvailableTime());
    setTimeError(false);
  };

  // Add useEffect to monitor tasks state
  useEffect(() => {
    console.log('Tasks state updated:', tasks);
  }, [tasks]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingTask) {
        updateTask();
      } else {
        addTask('custom');
      }
    }
  };

  if (!date) return null;

  const sortedTasks = [...tasks].sort((a, b) => a.time.localeCompare(b.time));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "max-w-2xl",
        isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            "text-2xl font-semibold",
              isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
            )}>
            {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Add/Edit Task Section */}
          <div className="flex gap-2">
            <div className="relative w-[100px]">
            <Input
              placeholder="HH:mm"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
                onKeyDown={handleKeyDown}
              className={cn(
                  "w-full pr-8",
                isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : "bg-white",
                timeError && "border-red-500"
              )}
            />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full w-8 text-[#818881] hover:text-[#CCF751]"
                onClick={() => setSelectedTime(getNextAvailableTime())}
                title="Reset to next available time"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Input
              placeholder={editingTask ? "Edit task..." : "Add a new task..."}
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                "flex-1",
                isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : "bg-white"
              )}
            />
            {editingTask ? (
              <>
                <Button
                  onClick={updateTask}
                  disabled={!newTaskTitle.trim() || timeError}
                  className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
                >
                  Update
                </Button>
                <Button
                  onClick={cancelEditing}
                  variant="outline"
                  className={isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                    : "text-gray-600 hover:text-gray-900"
                  }
                >
                  Cancel
                </Button>
              </>
            ) : (
            <Button
              onClick={() => {
                addTask('custom');
                // Time will be automatically reset by the effect, but let's make it explicit
                setTimeout(() => setSelectedTime(getNextAvailableTime()), 0);
              }}
              disabled={!newTaskTitle.trim() || timeError}
              className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
            >
              Add Task
            </Button>
            )}
          </div>
          {timeError && (
            <p className="text-sm text-red-500">
              Please enter time in HH:mm format (e.g., 09:15)
            </p>
          )}

          {/* Tasks List */}
          <div className="space-y-2">
            {sortedTasks.map((task) => (
              <div
                key={task.id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg border",
                  isDarkMode ? "border-[#818881] bg-[#818881]/10" : "border-gray-200 bg-gray-50",
                  task.completed && "opacity-50"
                )}
              >
          <Button
            variant="ghost"
            size="icon"
                  className="cursor-grab"
          >
                  <GripVertical className="h-4 w-4" />
          </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleTaskCompletion(task.id)}
                  className={cn(
                    "shrink-0",
                    task.completed ? "text-[#CCF751]" : "text-[#818881]"
                  )}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">{task.time}</span>
                <span className={cn(
                  "flex-1 text-sm",
                  task.completed && "line-through"
                )}>
                  {task.title}
                  </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => startEditingTask(task)}
                  className="text-[#CCF751] hover:text-[#CCF751]/80 hover:bg-[#CCF751]/10"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteTask(task.id)}
                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-400/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                </div>
              ))}
            {tasks.length === 0 && (
              <p className={cn(
                "text-sm text-center py-4",
                isDarkMode ? "text-[#818881]" : "text-gray-500"
              )}>
                No tasks scheduled for this day
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            variant="outline"
            onClick={onClose}
                    className={cn(
                      isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 